import { Mesh, MeshBasicMaterial, SphereGeometry, Color } from "three";
import { convertToNumber3, convertToNumber4, ProximitySphere, SelectionHighlightFacet } from "flavr-common";
import { now } from "@messages/timing";
import { CollisionShapePrimitive, RequestIntersectionsWithPrimitiveCollisionShapeMessage, ResponseIntersectionsWithPrimitiveCollisionShapeMessage } from "@messages/selection";
import { localPlayerContainer } from "@modules/localplayer/LocalPlayerContainer";
import { FacetRenderer } from "../renderable/Renderable";
import { COLOR_FLAVIA_BLUE, COLOR_FOREST_GREEN } from "../../../ui/style/predefined-colors";
import { modelPool } from "@modules/model";
import { configurePostprocessingOverlay } from "../postprocessing/PostprocessingOverlay";
const COLOR_IDLE = new Color(COLOR_FLAVIA_BLUE);
const COLOR_INTERSECTING = new Color(COLOR_FOREST_GREEN);
const SPHERE_GEOMETRY = new SphereGeometry(1, 16, 10);
const SPHERE_MATERIAL = new MeshBasicMaterial({ color: COLOR_IDLE, transparent: true, opacity: 0.2, depthTest: false, depthWrite: false });
const NEXT_INTERSECTION_UPDATE_TIME = 1000 / 10;
export class ProximitySphereRenderable extends FacetRenderer {
    constructor(owner) {
        super(owner);
        this.owner = owner;
        this.lastIntersectionUpdate = 0;
    }
    destroy() {
        if (this.sphereMesh !== undefined) {
            this.owner.removeObjectFromPersistentRoot(this.sphereMesh);
            this.sphereMesh = undefined;
        }
        if (this.sphereMaterial !== undefined) {
            this.sphereMaterial.dispose();
            this.sphereMaterial = undefined;
        }
        this.owner.renderer.removeAnimationCallback(this.owner.modelUID, ProximitySphere);
    }
    setVisible(visible) {
        if (this.sphereMesh !== undefined) {
            this.sphereMesh.visible = visible;
        }
    }
    updateFacet(facet) {
        if (this.sphereMesh === undefined) {
            this.sphereMaterial = SPHERE_MATERIAL.clone();
            this.sphereMesh = new Mesh(SPHERE_GEOMETRY, this.sphereMaterial);
            configurePostprocessingOverlay(this.sphereMesh);
            this.owner.addObjectToPersistentRoot(this.sphereMesh);
            this.owner.renderer.addAnimationCallback(this.owner.modelUID, ProximitySphere, () => {
                this.updateIntersections();
            });
        }
        this.sphereMesh.scale.setScalar(facet.radius * localPlayerContainer.getPlatformScale());
        this.sphereMesh.matrixWorldNeedsUpdate = true;
    }
    updateTransform() {
        if (this.sphereMesh === undefined || this.facet === undefined)
            return;
        this.sphereMesh.position.copy(this.owner.worldPosition);
        this.sphereMesh.scale.setScalar(this.facet.radius * localPlayerContainer.getPlatformScale());
        this.sphereMesh.matrixWorldNeedsUpdate = true;
    }
    updateIntersections() {
        if (this.facet === undefined)
            return;
        if (now() - this.lastIntersectionUpdate <= NEXT_INTERSECTION_UPDATE_TIME)
            return;
        this.lastIntersectionUpdate = now();
        this.owner.renderer
            .getConnection()
            .askByMessageType(new RequestIntersectionsWithPrimitiveCollisionShapeMessage(convertToNumber3(this.owner.worldPosition), convertToNumber4(this.owner.worldOrientation), localPlayerContainer.getPlatformScale(), CollisionShapePrimitive.SPHERE, this.facet.radius), ResponseIntersectionsWithPrimitiveCollisionShapeMessage)
            .then(msg => { this.handleIntersectionResponse(msg); })
            .catch(e => { console.error(e); });
    }
    handleIntersectionResponse(msg) {
        if (this.sphereMaterial === undefined)
            return;
        const isSelectable = (intersectionResult) => {
            var _a;
            return ((_a = modelPool
                .getModel(intersectionResult.uid)) === null || _a === void 0 ? void 0 : _a.getFacet(SelectionHighlightFacet)) !== undefined;
        };
        const hasSelectables = msg.selectables
            .filter(intersectionResult => isSelectable(intersectionResult))
            .length > 0;
        this.sphereMaterial.color = hasSelectables ? COLOR_INTERSECTING : COLOR_IDLE;
    }
}
